import { isExternalUrl } from '../pipes/markdown.pipe';

/**
 * Lift the agent's calls to action out of an answer, so they can be offered as
 * controls instead of as links buried in a sentence.
 *
 * The distinction the product draws (INEXP-2616): a reference belongs in the
 * prose, a next step does not. An offering's own page is a reference — the
 * reader may or may not want it, and it reads naturally mid-sentence. "Suggest
 * it for the catalogue in IdeaHub" is a next step, and a next step set in the
 * middle of a paragraph reads as a footnote.
 *
 * **The rule is about shape, not about which URL.** A link is a call to action
 * when it stands alone on its own line *and* leaves the app. That means no list
 * of known addresses kept in step with the agent's own constants, and it means
 * an environment still running an older build simply shows the same link as a
 * link — so neither half of the change has to ship before the other.
 *
 * Three deliberate exclusions:
 *
 *  - A bullet (`- [label](url)`) is not a call to action. It is a line in a
 *    list, and a list of request routes would otherwise turn into a row of
 *    buttons, which is a menu rather than a next step.
 *  - An in-app path stays a link wherever it appears. `/offerings/…` is
 *    navigation inside the conversation, and the transcript's delegated click
 *    handler already routes it without a page load.
 *  - A line the model gave no usable name — a bare URL, or link text that is
 *    only the address again — stays put. The surrounding sentence is the only
 *    thing saying what it is for, and a control named "globaljira.roche.com"
 *    has lost that (2.4.4 Link Purpose). See `asCallToAction`.
 *
 * This runs on the raw Markdown, BEFORE `MarkdownPipe`, and never over its
 * output. That pipe's own docstring says why: its whitelist is the only thing
 * between model output and the DOM, and a second pass over
 * `bypassSecurityTrustHtml`-ed markup is a second place to get escaping wrong.
 * Here there is no markup yet — just lines of text.
 */
export interface AssistantCta {
  /** What the control says — always the model's own link text, never a URL. */
  label: string;
  /** Where it goes. Always external, always opened in a new tab. */
  url: string;
}

export interface SplitAnswer {
  /** The answer with the lifted lines removed, for the renderer. */
  body: string;
  /** What to offer as controls, in the order the model wrote them. */
  actions: AssistantCta[];
}

/**
 * At most two. A third is a menu, and the row already shares space with the
 * follow-up questions.
 */
const MAX_ACTIONS = 2;

/** The same link shape `MarkdownPipe` accepts, anchored to a whole line. */
const WHOLE_LINE_LINK = /^\[([^\]]+)\]\(([^)\s]+)\)$/;

/**
 * One line, if it is a call to action on its own.
 *
 * **A line that cannot be labelled is not lifted.** A bare URL, or link text
 * that is only the address again, would give a control named
 * "globaljira.roche.com" — which says neither where it goes nor why, failing
 * 2.4.4 Link Purpose as a control and reading as noise where the transcript's
 * live region appends it to the answer. Left in the prose it keeps the
 * surrounding sentence, which supplies exactly the purpose the label lacks.
 * The better name is the one the model already wrote, or none.
 */
function asCallToAction(line: string): AssistantCta | undefined {
  const link = WHOLE_LINE_LINK.exec(line);
  if (!link) {
    return undefined;
  }
  const [, rawLabel, url] = link;
  const label = rawLabel.trim();
  if (!isExternalUrl(url) || !label || label === url) {
    return undefined;
  }
  return { label, url };
}

/**
 * Split an answer into the prose to render and the actions to offer.
 *
 * With nothing to lift, `body` is the input unchanged — the same string, not a
 * rebuilt copy — so an answer that has no call to action cannot be altered by
 * passing through here.
 */
export function splitCallToActions(src: string | null | undefined): SplitAnswer {
  const text = src ?? '';
  const kept: string[] = [];
  const actions: AssistantCta[] = [];
  const seen = new Set<string>();
  let fenced = false;

  for (const line of text.split('\n')) {
    const trimmed = line.trim();

    // A fence toggles; an unterminated one leaves everything after it fenced,
    // which is the safe direction — the worst case is a call to action left in
    // the prose, never an example lifted out of a code block.
    if (trimmed.startsWith('```')) {
      fenced = !fenced;
      kept.push(line);
      continue;
    }

    const cta = fenced ? undefined : asCallToAction(trimmed);
    if (!cta) {
      kept.push(line);
      continue;
    }

    // Already offered: the line goes, because the reader still has the control.
    if (seen.has(cta.url)) {
      continue;
    }

    // Past the cap the line STAYS. Dropping it would delete a route nothing
    // renders — the one outcome worse than leaving it in the sentence.
    if (actions.length >= MAX_ACTIONS) {
      kept.push(line);
      continue;
    }

    seen.add(cta.url);
    actions.push(cta);
  }

  // Nothing lifted, nothing touched — not even the trailing whitespace the
  // tidy-up below would otherwise take. An answer this function had no opinion
  // about must come back exactly as it arrived.
  if (!actions.length) {
    return { body: text, actions: [] };
  }

  // Lifting the last line usually leaves the answer ending in blank lines. The
  // renderer ignores them; this keeps the string itself honest for anything
  // that measures or compares it.
  //
  // Note the Copy button is deliberately NOT fed this: it copies the message as
  // the agent wrote it, so an answer pasted elsewhere still carries the address
  // the control stands for.
  return { body: kept.join('\n').replace(/\s+$/, ''), actions };
}
